"use client";

import { useBoundStore } from "@/lib/stores/useBoundStore";
import { useTranslations } from "next-intl";
import styled from "styled-components";
import { useEffect } from "react";

const LegalContainer = styled.div<{ $onPage: boolean }>`
  height: fit-content;
  overflow: auto;
  max-height: 75vh;
  margin-bottom: 50px;
  padding: ${(props) => (props.$onPage ? "30px" : "10px")};

  h1 {
    margin: 0 0 20px 0;
  }

  h3 {
    margin: 20px 0 10px 0;
  }

  p span {
    color: ${(props) => props.theme.black};
    font-weight: bold;
    text-decoration: none !important;
  }

  li span {
    text-decoration: underline;
  }

  .center-align {
    text-align: center;
    width: 100%;
  }

  .upper {
    text-transform: uppercase;
    margin-bottom: 5px;
  }

  ol,
  ul {
    color: ${(props) => props.theme.black};
    margin: 10px 0 0 35px;
    font-size: 0.95rem;

    li {
      margin-bottom: 5px;
    }
  }

  p {
    color: ${(props) => props.theme.black};
    font-size: 0.95rem;
  }

  a {
    color: ${(props) => props.theme.link};

    &:hover {
      color: ${(props) => props.theme.black};
    }
  }

  @media (max-width: 425px) {
    max-height: 92vh;
    padding: ${(props) => (props.$onPage ? "20px" : "0")};
    padding: 0px 15px;
    font-size: 0.75rem !important;

    p,
    ol,
    ul {
      font-size: 0.75rem !important;
    }
  }
`;

export function USTermsOfUseContent({
  onPage,
  onOpenPrivacy,
}: {
  onPage?: boolean;
  onOpenPrivacy?: () => void;
}) {
  const t = useTranslations("termsOfAcceptance.usTermsOfUse");

  const setModalType = useBoundStore((state) => state.setModalType);
  const modalType = useBoundStore((state) => state.modalType);
  const setModalOpen = useBoundStore((state) => state.setModalOpen);

  // On the /legal page a caller supplies onOpenPrivacy to switch tabs; elsewhere
  // (e.g. AcceptTerms) the link opens the privacy modal as before.
  const openPrivacy = () => {
    if (onOpenPrivacy) {
      onOpenPrivacy();
      return;
    }
    setModalOpen(true);
    setModalType("patient-privacy");
  };

  useEffect(() => console.log(modalType), [modalType]);

  return (
    <LegalContainer $onPage={onPage || false}>
      <h1>{t("title")}</h1>
      <p style={{ fontStyle: "italic", textAlign: "end" }}>
        {t("lastUpdated")}
      </p>
      <br />
      <p>{t("intro")}</p>
      <br />
      <p>{t("intro2")}</p>
      <br />
      <p>
        {t.rich("intro3", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  openPrivacy();
                }}
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <a>{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("intendedFor")}</p>
      <br />
      <p>{t("arbitrationNotice")}</p>
      <br />
      <h3 style={{ textAlign: "center" }}>{t("tableOfContents")}</h3>
      <ol>
        <li>{t("tocItem1")}</li>
        <li>{t("tocItem2")}</li>
        <li>{t("tocItem3")}</li>
        <li>{t("tocItem4")}</li>
        <li>{t("tocItem5")}</li>
        <li>{t("tocItem6")}</li>
        <li>{t("tocItem7")}</li>
        <li>{t("tocItem8")}</li>
        <li>{t("tocItem9")}</li>
        <li>{t("tocItem10")}</li>
        <li>{t("tocItem11")}</li>
        <li>{t("tocItem12")}</li>
        <li>{t("tocItem13")}</li>
        <li>{t("tocItem14")}</li>
        <li>{t("tocItem15")}</li>
        <li>{t("tocItem16")}</li>
        <li>{t("tocItem17")}</li>
        <li>{t("tocItem18")}</li>
        <li>{t("tocItem19")}</li>
        <li>{t("tocItem20")}</li>
        <li>{t("tocItem21")}</li>
        <li>{t("tocItem22")}</li>
        <li>{t("tocItem23")}</li>
        <li>{t("tocItem24")}</li>
        <li>{t("tocItem25")}</li>
        <li>{t("tocItem26")}</li>
        <li>{t("tocItem27")}</li>
        <li>{t("tocItem28")}</li>
      </ol>
      <br />
      <h3>{t("section1Title")}</h3>
      <br />
      <p>{t("section1Para1")}</p>
      <br />
      <p>{t("section1Para2")}</p>
      <br />
      <h3>{t("section2Title")}</h3>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section2Subtitle")}</p>
      <p>{t("section2Para1")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section2Para2")}</p>
      <br />
      <h3>{t("section3Title")}</h3>
      <br />
      <p>
        {t.rich("section3Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span
                onClick={(e) => {
                  e.preventDefault();
                  openPrivacy();
                }}
                style={{ textDecoration: "none", cursor: "pointer" }}
              >
                <a>{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section4Title")}</h3>
      <br />
      <p>{t("section4Para")}</p>
      <br />
      <h3>{t("section5Title")}</h3>
      <br />
      <p>{t("section5Intro")}</p>
      <ul>
        <li>{t("section5UL1")}</li>
        <li>{t("section5UL2")}</li>
        <li>{t("section5UL3")}</li>
        <li>{t("section5UL4")}</li>
        <li>{t("section5UL5")}</li>
        <li>{t("section5UL6")}</li>
        <li>{t("section5UL7")}</li>
        <li>{t("section5UL8")}</li>
        <li>{t("section5UL9")}</li>
        <li>{t("section5UL10")}</li>
        <li>{t("section5UL11")}</li>
        <li>{t("section5UL12")}</li>
        <li>{t("section5UL13")}</li>
        <li>{t("section5UL14")}</li>
      </ul>
      <p>{t("section5Para")}</p>
      <br />
      <h3>{t("section6Title")}</h3>
      <br />
      <p>{t("section6Para1")}</p>
      <br />
      <h3>{t("section7Title")}</h3>
      <br />
      <p>{t("section7Para1")}</p>
      <br />
      <p>{t("section7Para2")}</p>
      <br />
      <p>{t("section7Para3")}</p>
      <br />
      <h3>{t("section8Title")}</h3>
      <br />
      <p>{t("section8Para1")}</p>
      <br />
      <p>
        {t.rich("section8Para2", {
          link: (chunks) => (
            <span style={{ textDecoration: "none", fontWeight: "bold" }}>
              {chunks}
            </span>
          ),
        })}
      </p>
      <br />
      <p>{t("section8Para3")}</p>
      <br />
      <p>{t("section8Para4")}</p>
      <br />
      <h3>{t("section9Title")}</h3>
      <br />
      <p>{t("section9Para")}</p>
      <br />
      <h3>{t("section10Title")}</h3>
      <br />
      <p>{t("section10Para")}</p>
      <br />
      <h3>{t("section11Title")}</h3>
      <br />
      <p>{t("section11Para")}</p>
      <br />
      <h3>{t("section12Title")}</h3>
      <br />
      <p>{t("section12Para")}</p>
      <br />
      <h3>{t("section13Title")}</h3>
      <br />
      <p>{t("section13Para")}</p>
      <br />
      <h3>{t("section14Title")}</h3>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section14Para1")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section14Para2")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section14Para3")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section14Para4")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section14Para5")}</p>
      <br />
      <p>
        {t.rich("section14Para6", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return (
              <span style={{ textDecoration: "none" }}>
                <a href={`mailto:${text}`}>{text}</a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section15Title")}</h3>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section15Para1")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section15Para2")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section15Para3")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section15Para4")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section15Para5")}</p>
      <br />
      <h3>{t("section16Title")}</h3>
      <br />
      <p>{t("section16Para")}</p>
      <br />
      <h3>{t("section17Title")}</h3>
      <br />
      <p>{t("section17Para")}</p>
      <br />
      <h3>{t("section18Title")}</h3>
      <br />
      <p>{t("section18Para1")}</p>
      <br />
      <p>{t("section18Para2")}</p>
      <br />
      <p>
        {t.rich("section18Para3", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section19Title")}</h3>
      <br />
      <p>{t("section19Para")}</p>
      <br />
      <h3>{t("section20Title")}</h3>
      <br />
      <p>{t("section20Para")}</p>
      <br />
      <h3>{t("section21Title")}</h3>
      <br />
      <p>
        {t.rich("section21Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a href={text} target="_blank" rel="noopener noreferrer">
                  {text}
                </a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section22Title")}</h3>
      <br />
      <p>
        {t.rich("section22Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            if (/^[\d\s\-()]+$/.test(text.trim())) {
              return <a href={`tel:${text.replace(/[\s()-]/g, "")}`}>{text}</a>;
            }
            return (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section23Title")}</h3>
      <br />
      <p>
        {t.rich("section23Para1", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            );
          },
        })}
      </p>
      <p style={{ fontWeight: "bold", marginTop: "10px" }}>
        {t("section23Address1")}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("section23Address2")}</p>
      <p style={{ fontWeight: "bold" }}>{t("section23Address3")}</p>
      <p style={{ fontWeight: "bold" }}>{t("section23Address4")}</p>
      <br />
      <p>
        {t.rich("section23Para2", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return (
              <span style={{ textDecoration: "none", fontWeight: "bold" }}>
                {chunks}
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section24Title")}</h3>
      <br />
      <p>{t("section24Para")}</p>
      <br />
      <h3>{t("section25Title")}</h3>
      <br />
      <p>{t("section25Para")}</p>
      <br />
      <h3>{t("section26Title")}</h3>
      <br />
      <p>
        {t.rich("section26Para", {
          link: (chunks) => {
            const text = chunks!.toString();
            return (
              <span style={{ textDecoration: "none" }}>
                <a
                  href={`https://${text}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  {text}
                </a>
              </span>
            );
          },
        })}
      </p>
      <br />
      <h3>{t("section27Title")}</h3>
      <br />
      <p>{t("section27Para1")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section27AppleTitle")}</p>
      <p>{t("section27ApplePara")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>{t("section27GoogleTitle")}</p>
      <p>{t("section27GooglePara")}</p>
      <br />
      <h3>{t("section28Title")}</h3>
      <br />
      <p>{t("section28Para")}</p>
      <p style={{ fontWeight: "bold", marginTop: "10px" }}>
        {t("contactCompany")}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("contactAddress")}</p>
      <p style={{ fontWeight: "bold" }}>{t("contactCity")}</p>
      <br />
      <p style={{ fontWeight: "bold" }}>
        {t.rich("contactPhone", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (/^[\d\s\-()]+$/.test(text.trim())) {
              return <a href={`tel:${text.replace(/[\s()-]/g, "")}`}>{text}</a>;
            }
            return chunks;
          },
        })}
      </p>
      <p style={{ fontWeight: "bold" }}>{t("contactHours")}</p>
      <p style={{ fontWeight: "bold" }}>
        {t.rich("contactEmail", {
          link: (chunks) => {
            const text = chunks!.toString();
            if (text.includes("@")) {
              return <a href={`mailto:${text}`}>{text}</a>;
            }
            return chunks;
          },
        })}
      </p>
    </LegalContainer>
  );
}
